import { api } from "@/lib/apiClient";
import type { AbsenceType } from "@/lib/employeeAbsences";
import { getAbsenceTypeLabel } from "@/lib/employeeAbsences";

export type BridgeWednesdaySide = "before" | "after";

export type CalendarDayKind = "holiday" | "bridge" | "betriebsferien";

export interface EmployeeTimeCalendarSettings {
  bundesland: string;
  bridge_days_enabled: boolean;
  bridge_default_absence_type: AbsenceType;
  bridge_wednesday_double: boolean;
  bridge_wednesday_side: BridgeWednesdaySide;
  updated_at: string | null;
  updated_by_login: string | null;
}

export interface GermanStateOption {
  code: string;
  name: string;
}

export interface EmployeeCalendarDay {
  work_date: string;
  kind: CalendarDayKind;
  label: string;
  default_absence_type: AbsenceType | null;
  effective_absence_type: AbsenceType | null;
  is_override: boolean;
  works_instead: boolean;
  absence_request_id: number | null;
  absence_request_status: string | null;
  is_bridge_default?: boolean;
  day_fraction?: number;
  half_day_part?: "am" | "pm" | null;
}

/** Brückentag oder Betriebsferien – gleicher Override-/Antrag-Flow. */
export function isAutoAbsenceCalendarDay(day: EmployeeCalendarDay | null | undefined): boolean {
  return day?.kind === "bridge" || day?.kind === "betriebsferien";
}

export function calendarKindLabel(kind: CalendarDayKind): string {
  if (kind === "holiday") return "Feiertag";
  if (kind === "betriebsferien") return "Betriebsferien";
  return "Brückentag";
}

export interface EmployeeCalendarYear {
  year: number;
  bundesland: string;
  bridge_days_enabled: boolean;
  bridge_default_absence_type: AbsenceType;
  days: EmployeeCalendarDay[];
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function getCalendarSettings(): Promise<EmployeeTimeCalendarSettings> {
  return api.get("/employee-times/calendar-settings");
}

export function updateCalendarSettings(
  payload: Omit<EmployeeTimeCalendarSettings, "updated_at" | "updated_by_login">,
): Promise<EmployeeTimeCalendarSettings> {
  return api.put("/employee-times/calendar-settings", payload);
}

export function listGermanStates(): Promise<GermanStateOption[]> {
  return api.get("/employee-times/calendar-settings/states");
}

export function getCalendarDays(params: {
  year: number;
  user_login?: string;
}): Promise<EmployeeCalendarYear> {
  return api.get(`/employee-times/calendar-days${buildQuery(params)}`);
}

export function upsertBridgeDayOverride(payload: {
  work_date: string;
  absence_type?: AbsenceType | null;
  note?: string;
  user_login?: string;
}): Promise<{
  id: number;
  user_login: string;
  work_date: string;
  absence_type: AbsenceType | null;
  note: string | null;
}> {
  return api.put("/employee-times/bridge-day-overrides", payload);
}

export function deleteBridgeDayOverride(params: {
  work_date: string;
  user_login?: string;
}): Promise<{ status: string; id: number }> {
  return api.del(`/employee-times/bridge-day-overrides${buildQuery(params)}`);
}

export function calendarDaysToMap(days: EmployeeCalendarDay[]): Map<string, EmployeeCalendarDay> {
  return new Map(days.map((d) => [d.work_date.slice(0, 10), d]));
}

export function formatCalendarDayShort(day: EmployeeCalendarDay): string {
  if (day.kind === "holiday") return "FT";
  if (day.works_instead) return "↗";
  const half = (day.day_fraction ?? 1) < 1 ? "½" : "";
  if (day.kind === "betriebsferien") {
    if (day.effective_absence_type && day.effective_absence_type !== "urlaub") {
      return `${day.effective_absence_type.slice(0, 2).toUpperCase()}${half}`;
    }
    return `BF${half}`;
  }
  if (day.effective_absence_type) {
    const t = day.effective_absence_type;
    if (t === "urlaub") return "B";
    return t.slice(0, 2).toUpperCase();
  }
  return "B";
}

export function calendarDayTitle(day: EmployeeCalendarDay): string {
  if (day.kind === "holiday") return day.label;
  if (day.works_instead) return `${day.label} – geplant zu arbeiten`;
  if (day.is_bridge_default && day.effective_absence_type) {
    return `${day.label} – ${getAbsenceTypeLabel(day.effective_absence_type)} (automatisch genehmigt)`;
  }
  if (day.is_override && day.effective_absence_type) {
    return `${day.label} – ${getAbsenceTypeLabel(day.effective_absence_type)} (Abweichung)`;
  }
  if (day.effective_absence_type) {
    return `${day.label} – ${getAbsenceTypeLabel(day.effective_absence_type)}`;
  }
  return day.label;
}

export function calendarDayClassName(day: EmployeeCalendarDay): string {
  if (day.kind === "holiday") {
    return "bg-amber-500/20 text-amber-900 dark:text-amber-200 ring-1 ring-amber-500/30";
  }
  if (day.works_instead) {
    return "bg-fill-tertiary text-label-secondary ring-1 ring-separator border border-dashed";
  }
  if (day.is_override) {
    return "bg-violet-500/15 text-violet-800 dark:text-violet-200 ring-1 ring-violet-500/25";
  }
  if (day.kind === "betriebsferien") {
    return "bg-sky-500/15 text-sky-900 dark:text-sky-200 ring-1 ring-sky-500/25";
  }
  return "bg-indigo-500/15 text-indigo-800 dark:text-indigo-200 ring-1 ring-indigo-500/25";
}
